import {useFormContext} from "react-hook-form";

import {ImageInputField} from "@/components/form/fields/ImageInputField/ImageInputField.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {SelectField} from "@/components/form/fields/SelectField/SelectField.tsx";
import {SwitchField} from "@/components/form/fields/SwitchField.tsx";
import {TagsField} from "@/components/form/fields/TagsField/TagsField.tsx";
import {SupportLink} from "@/components/formSubtext/subComponents/SupportLink/SupportLink.tsx";
import {URL_ORIGINAL_WEBSITE} from "@/lib/links.ts";
import {SLAReportSort, SLAResponseTimeSectionSort} from "@/lib/queries/reports.ts";
import {ServiceTag} from "@/lib/types/checks.ts";
import {GroupChecksSelector} from "@/pages/checks/components/form/fields/legacy/GroupChecksSelector.tsx";
import {SectionGroup} from "@/pages/reports/components/SectionGroup.tsx";
import {AVAILABLE_DATE_RANGES, checksField} from "@/pages/reports/sla/SLAReportForm.utils.tsx";

interface SLAReportSettingsSectionProps {
  toggleSubform: () => void;
  tags: ServiceTag[];
  slaReportGlobalLogoUrl: string;
}

export const SLAReportSettingsSection = ({
  toggleSubform,
  tags,
  slaReportGlobalLogoUrl,
}: SLAReportSettingsSectionProps) => {
  const {watch} = useFormContext();

  return (
    <>
      <div className="pulseTextBody pulse-color-gray-800 my-4">
        An SLA Report allows your organization to report on specific Checks or systems for the purposes of SLA
        accountability.
        <SupportLink
          href="https://support.uptime.com/hc/en-us/articles/360017118860-Creating-an-Uptime-com-SLA-Report"
          openInZendesk={false}
          className="ms-1"
        />
      </div>
      <div className="row mb-4">
        <div className="col">
          <InputField name="name" label="Name" required />
        </div>
        <div className="col">
          <SelectField
            isClearable={false}
            options={AVAILABLE_DATE_RANGES}
            name="default_date_range"
            label="Default Date Range"
            tooltip="The Default Date Range is the date range that Reports will load with by default. This can be overridden with Scheduled Reports."
            required
          />
        </div>
      </div>
      <div className="row mb-4">
        <div className="col">
          <GroupChecksSelector
            name={checksField}
            label="Manually select Checks"
            tooltip="Select the Checks you would like to include in this group"
            toggleSubform={toggleSubform}
          />
        </div>
        <div className="col">
          <TagsField name="services_tags" label="Auto-select Checks by Tag" tags={tags} />
        </div>
      </div>
      <div className="row mb-4">
        <div className="col">
          <SectionGroup
            name="show_uptime_section"
            label="Uptime Section"
            helpText="Show a section with Uptime SLA statistics."
            hideErrorMessage
          >
            <SwitchField
              name="show_uptime_sla"
              label="Show Target SLA values on report"
              tooltip="Show the Target SLA values defined on each Check."
              disabled={!watch("show_uptime_section")}
            />
            <SwitchField
              name="filter_with_downtime"
              label="Show only Checks with downtime"
              tooltip="Exclude Checks with no downtime in the selected date range."
              disabled={!watch("show_uptime_section")}
            />
            <SwitchField
              name="filter_uptime_sla_violations"
              label="Show only Checks below SLA"
              tooltip="Exclude Checks that meet or exceed their defined SLA."
              disabled={!watch("show_uptime_section")}
            />
            <SelectField
              className="mt-3"
              options={[
                {value: SLAReportSort.BY_UPTIME, label: "By lowest Uptime"},
                {value: SLAReportSort.BY_SLA, label: "By difference from Check SLA"},
              ]}
              name="uptime_section_sort"
              label="Sorting"
              disabled={!watch("show_uptime_section")}
            />
          </SectionGroup>
        </div>
        <div className="col">
          <SectionGroup
            name="show_response_time_section"
            label="Response Time Section"
            helpText="Show a section with Response Time SLA statistics."
            hideErrorMessage
          >
            <SwitchField
              name="show_response_time_sla"
              label="Show Target SLA values on report"
              tooltip="Show the Target SLA values defined on each Check."
              disabled={!watch("show_response_time_section")}
            />
            <SwitchField
              name="filter_slowest"
              label="Limit to 5 Checks per type"
              tooltip="Include only the 5 lowest performing Checks of each type (e.g. HTTP, API, etc.) for the selected date range."
              disabled={!watch("show_response_time_section")}
            />
            <SwitchField
              name="filter_response_time_sla_violations"
              label="Show only Checks below SLA"
              tooltip="Exclude Checks that meet or exceed their defined SLA."
              disabled={!watch("show_response_time_section")}
            />
            <SelectField
              className="mt-3"
              options={[
                {value: SLAResponseTimeSectionSort.BY_RESPONSE_TIME, label: "By slowest response time"},
                {value: SLAResponseTimeSectionSort.BY_SLA, label: "By difference from Check SLA"},
              ]}
              name="response_time_section_sort"
              label="Sorting"
              disabled={!watch("show_response_time_section")}
            />
          </SectionGroup>
        </div>
      </div>
      <div className="row mb-4">
        <div className="col">
          <ImageInputField
            variant="report"
            name="logo"
            label="Report Logo"
            helpText={
              slaReportGlobalLogoUrl
                ? "Currently using the default report logo specified in Global Report Settings."
                : "Currently using the Uptime.com default logo. Upload an image to override for this report."
            }
            fallbackPreviewUrl={
              slaReportGlobalLogoUrl
                ? `${URL_ORIGINAL_WEBSITE}${slaReportGlobalLogoUrl}`
                : `${URL_ORIGINAL_WEBSITE}/static/img/global/logo-uptime-pdf.jpg`
            }
          />
        </div>
      </div>
    </>
  );
};
